import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Switch } from '../ui/switch';
import {
  AlertCircle, ChevronRight, ChevronLeft, Check, Loader2, X, Search, Plus, Trash2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/formatters';
import { expectedPayoffDate, termUnitLabel } from '../../lib/termDates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PATCH 3 (Jul 2026) — wizard redesign. Submits ONE atomic request to
// POST /api/deals/intake (routes/deal_intake.py): deal + refi payoffs
// (many→one) + partner syndication + partner_commissions ledger row.
// Drop-in replacement: same default export, same props from pages/Deals.js
// (onSubmit is accepted but no longer used — the wizard owns the network
// call so the write is atomic; parent's onCancel closes the dialog).
const STEPS = [
  { id: 1, title: 'Merchant', description: 'Who is this deal for?' },
  { id: 2, title: 'Money', description: 'Advance, rate & refi' },
  { id: 3, title: 'Program', description: 'Funder, commission & partner' },
  { id: 4, title: 'Review', description: 'Confirm & create' },
];

// ── Step indicator ───────────────────────────────────────────────────────
const StepIndicator = ({ current }) => (
  <div className="flex items-center justify-center gap-1 mb-6" data-testid="step-indicator">
    {STEPS.map((step, i) => (
      <React.Fragment key={step.id}>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
          step.id === current ? 'bg-blue-600 text-white font-medium' :
          step.id < current ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
        }`}>
          {step.id < current ? <Check className="h-3.5 w-3.5" /> : <span className="font-mono">{step.id}</span>}
          <span className="hidden sm:inline">{step.title}</span>
        </div>
        {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-gray-300" />}
      </React.Fragment>
    ))}
  </div>
);

// ── Shared date math ─────────────────────────────────────────────────────
// estimated_end_date by payment frequency (Josh 10/01): term is the number of
// PAYMENTS in the frequency's unit — business days / weeks / bi-weekly / months.
// Replaces funding_date + ceil(term × 1.4) calendar days, which turned a 13-week
// deal into a 19-day one. Always computed and always sent.
export const computeEstimatedEndDate = (fundingDate, termDays, frequency = 'Daily') =>
  expectedPayoffDate(fundingDate, termDays, frequency);

// ── Step 1: Merchant ─────────────────────────────────────────────────────
// Typeahead (BL-018 Ship A, preserved) + PATCH 3 inline merchant creation:
// name + shortcode, POST /api/contacts as a Merchant, without leaving the
// wizard. Backend enforces unique shortcode (409).
// Identity fields the contract pre-fill may seed onto a NEW merchant (mirrors
// backend contracts.MERCHANT_PREFILL_KEYS minus company_name, which the operator types).
const MERCHANT_PREFILL_KEYS = ['dba', 'ein', 'state_of_incorporation', 'business_address', 'business_city', 'business_state', 'business_zip', 'phone', 'email'];
const pickMerchantPrefill = (m) => Object.fromEntries(MERCHANT_PREFILL_KEYS.filter(k => m && m[k]).map(k => [k, m[k]]));

const InlineMerchantCreate = ({ initialName, onCreated, onDismiss, merchantPrefill }) => {
  const { getAuthHeaders } = useAuth();
  const [name, setName] = useState(initialName || '');
  const [shortcode, setShortcode] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const create = async () => {
    setErr('');
    if (!name.trim() || name.trim().length < 2) { setErr('Merchant name is required (min 2 chars)'); return; }
    if (!shortcode.trim()) { setErr('Shortcode is required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/contacts`, {
        ...pickMerchantPrefill(merchantPrefill),
        company_name: name.trim(),
        shortcode: shortcode.trim().toUpperCase(),
        is_merchant: true,
        contact_types: ['Merchant'],
      }, { headers: getAuthHeaders() });
      toast.success(`Merchant "${res.data.company_name}" created`);
      onCreated(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'Failed to create merchant');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 p-3 rounded-md border border-blue-300 bg-blue-50 space-y-2" data-testid="wizard-merchant-inline-create">
      <div className="text-sm font-medium text-blue-900">Create a new merchant contact</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Merchant name *</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Company name" data-testid="wizard-merchant-create-name" />
        </div>
        <div>
          <Label className="text-xs">Shortcode * (unique)</Label>
          <Input value={shortcode} onChange={e => setShortcode(e.target.value.toUpperCase())} placeholder="e.g. CAM" data-testid="wizard-merchant-create-shortcode" />
        </div>
      </div>
      {Object.keys(pickMerchantPrefill(merchantPrefill)).length > 0 && (
        <div className="text-xs text-blue-800" data-testid="wizard-merchant-create-prefill-note">
          From the contract: {Object.keys(pickMerchantPrefill(merchantPrefill)).map(k => k.replace(/_/g, ' ')).join(', ')} will be saved on this merchant.
        </div>
      )}
      {err && <div className="text-xs text-red-600" data-testid="wizard-merchant-create-error">{err}</div>}
      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={create} disabled={saving} data-testid="wizard-merchant-create-submit">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
          Create &amp; link
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>Cancel</Button>
      </div>
    </div>
  );
};

const MerchantTypeahead = ({ form, onSelect, onClear, onAcknowledgeNoMatch }) => {
  const { getAuthHeaders } = useAuth();
  const [search, setSearch] = useState(form.deal_name || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastQueriedAt, setLastQueriedAt] = useState(0);
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  const linked = !!form.company_id;
  const showNoMatchPanel = !linked && !creating
    && !loading
    && search.trim().length >= 3
    && lastQueriedAt > 0
    && results.length === 0;

  useEffect(() => {
    if (!form.company_id && !form.deal_name && search) setSearch('');
  }, [form.company_id, form.deal_name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim();
    if (q.length < 2 || linked) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/contacts/typeahead`, {
          params: { q, type: 'merchant', limit: 10 },
          headers: getAuthHeaders(),
        });
        setResults(Array.isArray(r.data) ? r.data : []);
        setLastQueriedAt(Date.now());
      } catch (_e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, linked, getAuthHeaders]);

  const handlePick = (row) => {
    onSelect(row);
    setSearch(row.company_name || '');
    setOpen(false);
    setResults([]);
    setCreating(false);
  };

  const handleClear = () => {
    setSearch('');
    setResults([]);
    setOpen(false);
    setCreating(false);
    onClear();
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="wizard-merchant-typeahead">
      <div className={`flex items-center border rounded-md bg-white overflow-hidden transition-all ${open ? 'ring-2 ring-ring' : ''}`}>
        <Search className="h-4 w-4 ml-3 text-muted-foreground" />
        <input
          className="flex-1 h-9 px-2 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); if (linked) onClear(); }}
          onFocus={() => setOpen(true)}
          placeholder="Start typing a merchant name…"
          autoComplete="off"
          data-testid="wizard-merchant-typeahead-input"
        />
        <div className="flex items-center pr-2 gap-1">
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" data-testid="wizard-merchant-typeahead-loading" />}
          {(search || linked) && (
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground p-0.5 rounded"
              onClick={handleClear}
              tabIndex={-1}
              data-testid="wizard-merchant-typeahead-clear"
              aria-label="Clear merchant selection"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {linked && (
        <div className="mt-1.5 text-xs text-emerald-700 flex items-center gap-1" data-testid="wizard-merchant-linked">
          <Check className="h-3 w-3" /> Linked to contact{form.deal_shortcode ? ` · ${form.deal_shortcode}` : ''}
        </div>
      )}

      {open && !linked && results.length > 0 && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border rounded-md shadow-md max-h-72 overflow-auto" data-testid="wizard-merchant-typeahead-results">
          {results.map((r) => (
            <button
              type="button"
              key={r.id}
              onClick={() => handlePick(r)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 focus:bg-slate-100 focus:outline-none flex items-center justify-between"
              data-testid={`wizard-merchant-typeahead-option-${r.shortcode || r.id}`}
            >
              <span className="font-medium text-slate-900">{r.company_name}</span>
              {r.shortcode && <span className="text-xs font-mono text-slate-500 ml-2">{r.shortcode}</span>}
            </button>
          ))}
        </div>
      )}

      {/* PATCH 3: inline creation — always reachable, no wizard exit */}
      {!linked && !creating && (
        <button
          type="button"
          className="mt-1.5 text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
          onClick={() => setCreating(true)}
          data-testid="wizard-merchant-create-toggle"
        >
          <Plus className="h-3 w-3" /> Create new merchant
        </button>
      )}
      {creating && (
        <InlineMerchantCreate
          initialName={search.trim()}
          onCreated={handlePick}
          onDismiss={() => setCreating(false)}
          merchantPrefill={form.merchant_prefill}
        />
      )}

      {showNoMatchPanel && (
        <div className="mt-2 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm space-y-2" data-testid="wizard-merchant-no-match-panel">
          <div>
            <div className="font-medium">No matching contact found for "{search.trim()}".</div>
            <div className="mt-1 text-xs">
              Create the merchant inline above, or proceed without a contact link (link later via Data Health).
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => onAcknowledgeNoMatch(search.trim())} data-testid="wizard-merchant-no-match-proceed">
              Create deal anyway
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setCreating(true)} data-testid="wizard-merchant-no-match-create">
              Create merchant contact
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const Step1 = ({ form, onChange, onSelectMerchant, onClearMerchant, onAcknowledgeNoMatch }) => (
  <div className="space-y-4" data-testid="wizard-step-1">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label>Deal ID *</Label>
        <Input value={form.deal_id} onChange={e => onChange('deal_id', e.target.value)} placeholder="e.g. R200, E301" data-testid="wizard-deal-id" />
      </div>
      <div>
        <Label>Business / Deal Name *</Label>
        <MerchantTypeahead
          form={form}
          onSelect={onSelectMerchant}
          onClear={onClearMerchant}
          onAcknowledgeNoMatch={onAcknowledgeNoMatch}
        />
      </div>
      <div>
        <Label>Shortcode *</Label>
        <Input
          value={form.deal_shortcode}
          onChange={e => onChange('deal_shortcode', e.target.value)}
          placeholder="e.g. CAM, JCA"
          data-testid="wizard-shortcode"
        />
      </div>
    </div>
  </div>
);

// ── Step 2: Money ────────────────────────────────────────────────────────
const PRIMARY_FREQS = ['Daily', 'Weekly'];
const MORE_FREQS = ['Bi-Weekly', 'Monthly'];  // 'Daily (Banking Days Only)' folded into Daily (Josh 10/01)

const sameTermUnit = (a, b) => termUnitLabel(a) === termUnitLabel(b);

// One refi row: old-deal typeahead (GET /api/deals?search=) showing the LIVE
// balance_with_fees, which auto-fills the row's payoff (editable).
const RefiRow = ({ row, index, onUpdate, onRemove, excludeIds }) => {
  const { getAuthHeaders } = useAuth();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (row.old_deal_id) return undefined;
    if (!q || q.trim().length < 2) { setResults([]); return undefined; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/deals`, { params: { search: q, limit: 10 }, headers: getAuthHeaders() });
        const data = Array.isArray(r.data) ? r.data : (r.data?.deals || []);
        setResults(data.filter(d => !excludeIds.includes(d.id)));
        setOpen(true);
      } catch (_e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, row.old_deal_id, getAuthHeaders, excludeIds]);

  const pick = (d) => {
    const live = parseFloat(d.balance_with_fees ?? d.with_fees ?? d.balance) || 0;
    onUpdate(index, {
      ...row,
      old_deal_id: d.id,
      deal_id_number: d.deal_id,
      label: `${d.deal_id} — ${d.deal_name || ''}`,
      balance_with_fees: live,
      payoff_amount: live > 0 ? live.toFixed(2) : '',
    });
    setOpen(false);
    setResults([]);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border rounded-md p-2 bg-white" data-testid={`wizard-refi-row-${index}`}>
      <div className="md:col-span-6" ref={wrapRef}>
        <Label className="text-xs">Old deal being paid off *</Label>
        {row.old_deal_id ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-9 flex items-center px-3 bg-slate-50 border rounded-md text-sm truncate" data-testid={`wizard-refi-selected-${index}`}>
              {row.label}
            </div>
            <Button type="button" variant="outline" size="sm"
              onClick={() => { onUpdate(index, { ...row, old_deal_id: '', deal_id_number: '', label: '', balance_with_fees: null, payoff_amount: '' }); setQ(''); }}>
              Change
            </Button>
          </div>
        ) : (
          <div className="relative">
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search prior deal (id or name)" data-testid={`wizard-refi-deal-search-${index}`} />
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-2 top-3 text-muted-foreground" />}
            {open && results.length > 0 && (
              <div className="absolute z-50 mt-1 left-0 right-0 bg-white border rounded-md shadow-md py-1 max-h-56 overflow-auto">
                {results.map(d => (
                  <button type="button" key={d.id}
                    onClick={() => pick(d)}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 flex items-center justify-between gap-2"
                    data-testid={`wizard-refi-option-${d.deal_id}`}>
                    <span className="truncate"><span className="font-mono">{d.deal_id}</span> — {d.deal_name} <span className="text-gray-400">({d.status})</span></span>
                    <span className="text-xs font-medium text-blue-700 whitespace-nowrap">
                      {formatCurrency(parseFloat(d.balance_with_fees ?? d.with_fees ?? d.balance) || 0)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="md:col-span-3">
        <Label className="text-xs">Live balance (w/ fees)</Label>
        <div className="h-9 flex items-center px-3 bg-gray-50 border rounded-md text-sm font-medium" data-testid={`wizard-refi-balance-${index}`}>
          {row.balance_with_fees != null ? formatCurrency(row.balance_with_fees) : '—'}
        </div>
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">Payoff *</Label>
        <Input
          type="number" step="0.01"
          value={row.payoff_amount}
          onChange={e => onUpdate(index, { ...row, payoff_amount: e.target.value })}
          placeholder="0.00"
          data-testid={`wizard-refi-payoff-${index}`}
        />
      </div>
      <div className="md:col-span-1 flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(index)} aria-label="Remove refi row" data-testid={`wizard-refi-remove-${index}`}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

const Step2 = ({ form, onChange, onMoneyChange, refiRows, onRefiUpdate, onRefiAdd, onRefiRemove, totals }) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const setFrequency = (nextFreq) => {
    const prevFreq = form.payment_frequency || 'Daily';
    if (nextFreq === prevFreq) { setMoreOpen(false); return; }
    const shouldClearTerm = form.term_days && !sameTermUnit(prevFreq, nextFreq);
    onChange('payment_frequency', nextFreq);
    if (shouldClearTerm) onMoneyChange('term_days', '');
    setMoreOpen(false);
  };

  const currentFreq = form.payment_frequency || 'Daily';
  const isMoreActive = MORE_FREQS.includes(currentFreq);
  const selectedIds = refiRows.map(r => r.old_deal_id).filter(Boolean);

  return (
    <div className="space-y-4" data-testid="wizard-step-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Advance Amount *</Label>
          <Input type="number" step="0.01" value={form.funding_amount} onChange={e => onMoneyChange('funding_amount', e.target.value)} placeholder="50000" data-testid="wizard-funding-amount" />
        </div>
        <div>
          <Label>Factor Rate *</Label>
          <Input type="number" step="0.01" value={form.rate} onChange={e => onMoneyChange('rate', e.target.value)} placeholder="1.35" data-testid="wizard-rate" />
        </div>
        <div>
          <Label>Payback (live, editable)</Label>
          <Input type="number" step="0.01" value={form.payback} onChange={e => onMoneyChange('payback', e.target.value)} placeholder="auto: advance × rate" data-testid="wizard-payback" />
        </div>
        <div className="md:col-span-2">
          <Label>Term ({termUnitLabel(currentFreq)})</Label>
          <div className="flex flex-wrap items-center gap-2">
            {PRIMARY_FREQS.map((f) => (
              <Button key={f} type="button" size="sm"
                variant={currentFreq === f ? 'default' : 'outline'}
                onClick={() => setFrequency(f)}
                data-testid={`wizard-freq-pill-${f.toLowerCase()}`}>
                {f}
              </Button>
            ))}
            <div ref={moreRef} className="relative inline-block">
              <Button type="button" size="sm"
                variant={isMoreActive ? 'default' : 'outline'}
                onClick={() => setMoreOpen(s => !s)}
                data-testid="wizard-freq-more">
                {isMoreActive ? currentFreq : 'More…'}
              </Button>
              {moreOpen && (
                <div className="absolute z-50 mt-1 left-0 bg-white border rounded-md shadow-md py-1 min-w-[220px]" data-testid="wizard-freq-more-menu">
                  {MORE_FREQS.map((f) => (
                    <button type="button" key={f} onClick={() => setFrequency(f)}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 ${currentFreq === f ? 'font-semibold text-blue-700' : ''}`}
                      data-testid={`wizard-freq-more-option-${f.toLowerCase().replace(/\s|\(|\)/g, '-')}`}>
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Input type="number" value={form.term_days} onChange={e => onMoneyChange('term_days', e.target.value)} placeholder="120" className="w-24" data-testid="wizard-term" />
          </div>
        </div>
        <div>
          <Label>Payment (live, editable)</Label>
          <Input type="number" step="0.01" value={form.payment_amount} onChange={e => onMoneyChange('payment_amount', e.target.value)} placeholder="auto: payback ÷ term" data-testid="wizard-payment-amount" />
        </div>
        <div>
          <Label>Funding Date *</Label>
          <Input type="date" value={form.funding_date} onChange={e => onChange('funding_date', e.target.value)} data-testid="wizard-funding-date" />
        </div>
        <div>
          <Label>Fees (origination)</Label>
          <Input type="number" step="0.01" value={form.fees} onChange={e => onMoneyChange('fees', e.target.value)} placeholder="0" data-testid="wizard-fees" />
        </div>
        <div>
          <Label>Net Advance</Label>
          <div className={`h-9 flex items-center px-3 border rounded-md text-sm font-medium ${totals.netAdvance >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="wizard-net-advance">
            {formatCurrency(totals.netAdvance)}
          </div>
        </div>
      </div>

      {/* Refinance — many→one: stacked old-deal rows */}
      <div className="border rounded-md p-3 bg-slate-50 space-y-2" data-testid="wizard-refi-section">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Refinancing old deals into this one?</span>
          {refiRows.length > 0 && (
            <span className="text-sm text-blue-700 font-medium" data-testid="wizard-refi-total">
              Total payoff: {formatCurrency(totals.totalPayoff)}
            </span>
          )}
        </div>
        {refiRows.map((row, i) => (
          <RefiRow
            key={row.key}
            row={row}
            index={i}
            onUpdate={onRefiUpdate}
            onRemove={onRefiRemove}
            excludeIds={selectedIds.filter(id => id !== row.old_deal_id)}
          />
        ))}
        <Button type="button" variant="outline" size="sm" onClick={onRefiAdd} data-testid="wizard-refi-add">
          <Plus className="h-3.5 w-3.5 mr-1" /> {refiRows.length === 0 ? 'Add a deal to pay off' : 'Add another deal'}
        </Button>
        {refiRows.length > 0 && (
          <p className="text-xs text-muted-foreground">
            On create, each row posts a payoff on its old deal and links the refi chain (payoffs sum into the new deal's refi balance payoff).
          </p>
        )}
      </div>
    </div>
  );
};

// ── Step 3: Program ──────────────────────────────────────────────────────
const Step3 = ({ form, onChange, brokers, funders, investors, partnerCalc }) => (
  <div className="space-y-5" data-testid="wizard-step-3">
    {/* (1) Funder */}
    <div>
      <Label>Who is funding this deal? *</Label>
      <Select value={form.funder_id} onValueChange={v => onChange('funder_id', v)}>
        <SelectTrigger data-testid="wizard-funder"><SelectValue placeholder="Select funder" /></SelectTrigger>
        <SelectContent>
          {(funders || []).map(f => <SelectItem key={f.id} value={f.id}>{f.company_name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>

    {/* (2) Commission */}
    <div className="border rounded-md p-3 space-y-3" data-testid="wizard-commission-block">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Who gets the commission? *</Label>
          <Select value={form.broker_id} onValueChange={v => onChange('broker_id', v)}>
            <SelectTrigger data-testid="wizard-broker"><SelectValue placeholder="Select broker / ISO" /></SelectTrigger>
            <SelectContent>
              {(brokers || []).map(b => <SelectItem key={b.id} value={b.id}>{b.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Points (% of advance)</Label>
          <Input type="number" step="0.01" value={form.commission_points} onChange={e => onChange('commission_points', e.target.value)} placeholder="e.g. 12" data-testid="wizard-commission-points" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!form.cash_commission_paid} onCheckedChange={v => onChange('cash_commission_paid', v)} data-testid="wizard-cash-commission-toggle" />
        <div>
          <div className="text-sm font-medium">Cash commission actually paid?</div>
          <div className="text-xs text-muted-foreground">Off = favor-funded → commission rate 0 is recorded on the deal.</div>
        </div>
      </div>
      {/* 09/29 (Josh): "when we fund a deal for an ISO there is a management fee, even in-house funding —
          always ask". Default ON when the chosen broker contact is flagged ISO. */}
      {form.broker_id && (
        <div className="flex items-center gap-3 pt-1 border-t" data-testid="wizard-mgmt-fee-block">
          <Switch checked={!!form.management_fee_on} onCheckedChange={v => onChange('management_fee_on', v)} data-testid="wizard-mgmt-fee-toggle" />
          <div className="flex-1">
            <div className="text-sm font-medium">Management fee on this deal? {(brokers || []).find(b => b.id === form.broker_id)?.is_iso ? <span className="text-xs text-purple-700">(ISO on file)</span> : null}</div>
            <div className="text-xs text-muted-foreground">2% upcharge, charged up front on its own line. Also the default for syndicators added to this deal.</div>
          </div>
          {form.management_fee_on && (
            <div className="w-24"><Label className="text-xs">Rate %</Label><Input type="number" step="0.01" min="0" value={form.management_fee_rate} onChange={e => onChange('management_fee_rate', e.target.value)} data-testid="wizard-mgmt-fee-rate" /></div>
          )}
        </div>
      )}
    </div>

    {/* (3) Co-funded deal (10/15 Josh — was "Partner deal"; stamps deal.cofunded_partner_id) */}
    <div className="border rounded-md p-3 bg-slate-50 space-y-3" data-testid="wizard-partner-block">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.is_partner_deal}
          onChange={e => onChange('is_partner_deal', e.target.checked)}
          data-testid="wizard-partner-checkbox"
        />
        <span className="text-sm font-medium">Co-funded deal (Milvado / Enod / …) — the partner brought the deal and pays the broker; PFS / DIB owe the partner their share of the commission</span>
      </label>

      {form.is_partner_deal && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Partner *</Label>
              <Select value={form.partner_contact_id} onValueChange={v => onChange('partner_contact_id', v)}>
                <SelectTrigger data-testid="wizard-partner-select"><SelectValue placeholder="Select partner (Investor contact)" /></SelectTrigger>
                <SelectContent>
                  {(investors || []).map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>PFS participation % *</Label>
              <Input type="number" step="0.01" min="0" max="100" value={form.pfs_participation_pct} onChange={e => onChange('pfs_participation_pct', e.target.value)} placeholder="0 – 100" data-testid="wizard-pfs-pct" />
            </div>
          </div>

          {/* Live derived panel */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-white border rounded-md p-3" data-testid="wizard-partner-derived">
            <div>
              <div className="text-xs text-gray-500">Deal class</div>
              <div className={`font-semibold ${partnerCalc.dealClass === 'management' ? 'text-purple-700' : 'text-slate-800'}`} data-testid="wizard-derived-class">
                {partnerCalc.dealClass}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Partner syndication</div>
              <div className="font-semibold" data-testid="wizard-derived-partner-pct">{partnerCalc.partnerPct}%</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Wire due from partner</div>
              <div className="font-semibold text-blue-700" data-testid="wizard-derived-wire-due">{formatCurrency(partnerCalc.wireDue)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">CM owed to partner</div>
              <div className="font-semibold text-emerald-700" data-testid="wizard-derived-cm-owed">{formatCurrency(partnerCalc.cmOwed)}</div>
            </div>
            <div className="col-span-2 md:col-span-4 text-xs text-muted-foreground">
              wire due = advance × {partnerCalc.partnerPct}% − fees × {partnerCalc.partnerPct}% · CM owed = {partnerCalc.effectivePoints}% pts × (advance × {partnerCalc.pfsPct}% PFS)
              {partnerCalc.dealClass === 'management' && ' · management deal (PFS at 0%) — no commission owed'}
            </div>
          </div>
        </>
      )}
    </div>
  </div>
);

// ── Step 4: Review ───────────────────────────────────────────────────────
const BigNumber = ({ label, value, tone, testId }) => (
  <div className="bg-white border rounded-md p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-lg font-bold ${tone || 'text-slate-900'}`} data-testid={testId}>{value}</div>
  </div>
);

const Step4 = ({
  form, onChange, totals, partnerCalc, brokers, funders, investors,
  recipients, recipientsLoading, selectedRecipientIds, onToggleRecipient, onToggleAllRecipients,
}) => {
  const funderName = (funders || []).find(f => f.id === form.funder_id)?.company_name || '—';
  const brokerName = (brokers || []).find(b => b.id === form.broker_id)?.company_name || '—';
  const partnerName = (investors || []).find(p => p.id === form.partner_contact_id)?.company_name || '—';
  return (
    <div className="space-y-4" data-testid="wizard-step-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <BigNumber label="Funded" value={formatCurrency(totals.funding)} testId="wizard-review-funded" />
        <BigNumber label="Net Advance" value={formatCurrency(totals.netAdvance)} tone={totals.netAdvance >= 0 ? 'text-green-700' : 'text-red-700'} testId="wizard-review-net-advance" />
        <BigNumber label="Payback" value={formatCurrency(totals.payback)} tone="text-blue-700" testId="wizard-review-payback" />
        <BigNumber label={`Payment (${form.payment_frequency})`} value={formatCurrency(totals.payment)} testId="wizard-review-payment" />
        {form.is_partner_deal && (
          <>
            <BigNumber label="Wire due from partner" value={formatCurrency(partnerCalc.wireDue)} tone="text-blue-700" testId="wizard-review-wire-due" />
            <BigNumber label="CM owed to partner" value={formatCurrency(partnerCalc.cmOwed)} tone="text-emerald-700" testId="wizard-review-cm-owed" />
          </>
        )}
        <BigNumber label="Deal class" value={partnerCalc.dealClass} tone={partnerCalc.dealClass === 'management' ? 'text-purple-700' : undefined} testId="wizard-review-class" />
      </div>

      <div className="border rounded-lg p-4 bg-gray-50 space-y-3" data-testid="wizard-review">
        <h4 className="font-semibold text-sm text-gray-700 uppercase tracking-wide">Deal Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-gray-500">Deal ID:</span> <span className="font-medium">{form.deal_id || '—'}</span></div>
          <div><span className="text-gray-500">Name:</span> <span className="font-medium">{form.deal_name || '—'}</span></div>
          <div>
            <span className="text-gray-500">Merchant:</span>{' '}
            <span className="font-medium" data-testid="wizard-review-merchant">
              {form.company_id
                ? <>Linked{form.deal_shortcode ? ` · ${form.deal_shortcode}` : ''}</>
                : <span className="text-amber-700">Unlinked</span>}
            </span>
          </div>
          <div><span className="text-gray-500">Factor:</span> <span className="font-medium">{form.rate || '—'}</span></div>
          <div><span className="text-gray-500">Term:</span> <span className="font-medium">{form.term_days ? `${form.term_days} ${termUnitLabel(form.payment_frequency)}` : '—'}</span></div>
          <div><span className="text-gray-500">Funded:</span> <span className="font-medium">{form.funding_date || '—'}</span></div>
          <div><span className="text-gray-500">Est. end:</span> <span className="font-medium" data-testid="wizard-review-est-end">{totals.estimatedEndDate || '—'}</span></div>
          <div><span className="text-gray-500">Funder:</span> <span className="font-medium">{funderName}</span></div>
          <div><span className="text-gray-500">Commission to:</span> <span className="font-medium">{brokerName}</span></div>
          <div><span className="text-gray-500">Points:</span> <span className="font-medium">{partnerCalc.effectivePoints}%{form.cash_commission_paid ? '' : ' (favor-funded → 0)'}</span></div>
          {form.is_partner_deal && (
            <div><span className="text-gray-500">Partner:</span> <span className="font-medium">{partnerName} @ {partnerCalc.partnerPct}%</span></div>
          )}
          {totals.refiCount > 0 && (
            <div><span className="text-gray-500">Refis in:</span> <span className="font-medium">{totals.refiCount} deal(s) · {formatCurrency(totals.totalPayoff)}</span></div>
          )}
        </div>
      </div>

      {/* 08/12 (Josh): refi — reuse the source deal's syndication book */}
      {totals.refiCount > 0 && (
        <label className="flex items-center gap-2 cursor-pointer border rounded-md p-3 bg-emerald-50 border-emerald-200">
          <input
            type="checkbox"
            checked={!!form.reuse_syndicators}
            onChange={e => onChange('reuse_syndicators', e.target.checked)}
            data-testid="wizard-reuse-syndicators"
          />
          <span className="text-sm">
            <span className="font-medium">Reuse same syndicators &amp; percentages</span>
            <span className="text-muted-foreground">
              {' '}— copies the refinanced deal's book onto this deal (advances re-sized to the new funding).
              {totals.refiCount > 1 ? ' With multiple source deals you\'ll pick which book on the Syndication tab.' : ''}
            </span>
          </span>
        </label>
      )}

      <label className="flex items-center gap-2 cursor-pointer border rounded-md p-3 bg-blue-50 border-blue-200">
        <input
          type="checkbox"
          checked={!!form.send_for_syndication}
          onChange={e => onChange('send_for_syndication', e.target.checked)}
          data-testid="wizard-send-for-syndication"
        />
        <span className="text-sm">
          <span className="font-medium">Send out for syndication after create</span>
          <span className="text-muted-foreground"> — opens the syndication offering page for this deal (allocate → request → confirm).</span>
        </span>
      </label>

      {/* PATCH 4 — checkbox list of syndicators the offer email goes to */}
      {form.send_for_syndication && (
        <div className="border rounded-md p-3 bg-white border-blue-200 space-y-2" data-testid="wizard-syndication-recipients">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Email this offer to</div>
            {(recipients || []).length > 0 && (
              <button
                type="button"
                className="text-xs text-blue-700 hover:underline"
                onClick={onToggleAllRecipients}
                data-testid="wizard-syndication-recipients-toggle-all"
              >
                {selectedRecipientIds.length === recipients.length ? 'Unselect all' : 'Select all'}
              </button>
            )}
          </div>
          {recipientsLoading && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading syndicators…
            </div>
          )}
          {!recipientsLoading && (recipients || []).length === 0 && (
            <div className="text-sm text-muted-foreground" data-testid="wizard-syndication-recipients-empty">
              No Investor contacts with an email address were found — the offer email will be skipped.
            </div>
          )}
          {!recipientsLoading && (recipients || []).length > 0 && (
            <div className="max-h-44 overflow-auto space-y-1">
              {recipients.map(r => (
                <label key={r.id} className="flex items-center gap-2 cursor-pointer text-sm px-1 py-0.5 rounded hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selectedRecipientIds.includes(r.id)}
                    onChange={() => onToggleRecipient(r.id)}
                    data-testid={`wizard-syndication-recipient-${r.id}`}
                  />
                  <span className="font-medium">{r.company_name || r.name}</span>
                  <span className="text-muted-foreground">— {r.email}</span>
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Message template: Settings → Email Templates → "Syndication Offer" (edit subject, body &amp; variables there).
          </p>
        </div>
      )}
    </div>
  );
};

// ── Validation ───────────────────────────────────────────────────────────
const validateStep = (step, form, refiRows) => {
  const errors = [];
  if (step === 1) {
    if (!form.deal_id.trim()) errors.push('Deal ID is required');
    if (!form.deal_name.trim()) errors.push('Business name is required');
    if (!form.deal_shortcode.trim()) errors.push('Shortcode is required');
  } else if (step === 2) {
    if (!form.funding_amount || parseFloat(form.funding_amount) <= 0) errors.push('Advance amount is required');
    if (!form.rate || parseFloat(form.rate) <= 0) errors.push('Factor rate is required');
    if (!form.payback || parseFloat(form.payback) <= 0) errors.push('Payback is required');
    if (!form.payment_amount || parseFloat(form.payment_amount) <= 0) errors.push('Payment amount is required');
    if (!form.funding_date) errors.push('Funding date is required');
    refiRows.forEach((row, i) => {
      if (!row.old_deal_id) errors.push(`Refi row ${i + 1}: select the old deal (or remove the row)`);
      if (!row.payoff_amount || parseFloat(row.payoff_amount) <= 0) errors.push(`Refi row ${i + 1}: payoff amount is required`);
    });
  } else if (step === 3) {
    if (!form.funder_id) errors.push('Funder is required');
    if (!form.broker_id) errors.push('Commission recipient (broker/ISO) is required');
    if (form.is_partner_deal) {
      if (!form.partner_contact_id) errors.push('Partner contact is required for a partner deal');
      const pct = parseFloat(form.pfs_participation_pct);
      if (form.pfs_participation_pct === '' || Number.isNaN(pct) || pct < 0 || pct > 100) {
        errors.push('PFS participation % must be between 0 and 100');
      }
    }
  } else if (step === 4) {
    if (!form.company_id && !form.acknowledged_no_match) {
      errors.push('Merchant contact is required — link or create one in Step 1, or use "Create deal anyway" on the no-match panel.');
    }
  }
  return errors;
};

// ── Main Wizard ──────────────────────────────────────────────────────────
// Props contract unchanged from the pre-PATCH-3 wizard (pages/Deals.js):
// { onSubmit, onCancel, brokers, funders, error }. onSubmit is intentionally
// NOT used — this wizard submits atomically to POST /api/deals/intake itself.
const DealWizard = ({ onCancel, brokers, funders, error: externalError }) => {
  const { getAuthHeaders } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [investors, setInvestors] = useState([]);

  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    // Step 1
    deal_id: '', deal_name: '', deal_shortcode: '', company_id: '',
    acknowledged_no_match: false,
    merchant_prefill: null,   // identity read from the contract PDF (see /agent/extract-contract)
    // Step 2
    funding_amount: '', rate: '', payback: '', payment_amount: '',
    term_days: '', funding_date: today, fees: '0',
    payment_frequency: 'Daily',
    // Step 3
    funder_id: '', broker_id: '',
    commission_points: '', cash_commission_paid: true,
    management_fee_on: false, management_fee_rate: '2',
    is_partner_deal: false, partner_contact_id: '', pfs_participation_pct: '',
    // Step 4
    send_for_syndication: false,
    reuse_syndicators: true,  // 08/12 (Josh): refi book carry-over, on by default
  });

  // Live-computed but user-editable: once the operator types into payback or
  // payment, the auto-calc stops overwriting that field.
  const [paybackTouched, setPaybackTouched] = useState(false);
  const [paymentTouched, setPaymentTouched] = useState(false);

  // Refi rows (many→one)
  const [refiRows, setRefiRows] = useState([]);
  const refiKeyRef = useRef(0);

  // Pre-fill from a contract PDF (handed over by the assistant widget via
  // sessionStorage). Runs once on mount, then clears the key so a later manual
  // "New Deal" doesn't silently reuse a stale contract. The operator still
  // reviews every field and saves — this only seeds the form. Refi rows arrive
  // already resolved (old_deal_id + payoff), so the many→one intake path works.
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    prefillAppliedRef.current = true;
    let saved;
    try {
      const raw = sessionStorage.getItem('prefillDealWizard');
      if (!raw) return;
      saved = JSON.parse(raw);
      sessionStorage.removeItem('prefillDealWizard');
    } catch {
      return;
    }
    if (!saved || typeof saved !== 'object') return;

    const str = (v) => (v === null || v === undefined ? '' : String(v));
    setForm(prev => ({
      ...prev,
      ...(saved.deal_id ? { deal_id: str(saved.deal_id) } : {}),
      ...(saved.company_id ? { company_id: str(saved.company_id), acknowledged_no_match: false } : {}),
      ...(saved.deal_name ? { deal_name: str(saved.deal_name) } : {}),
      ...(saved.deal_shortcode ? { deal_shortcode: str(saved.deal_shortcode) } : {}),
      ...(saved.funding_amount ? { funding_amount: str(saved.funding_amount) } : {}),
      ...(saved.rate ? { rate: str(saved.rate) } : {}),
      ...(saved.payback ? { payback: str(saved.payback) } : {}),
      ...(saved.payment_amount ? { payment_amount: str(saved.payment_amount) } : {}),
      ...(saved.term_days ? { term_days: str(saved.term_days) } : {}),
      ...(saved.payment_frequency ? { payment_frequency: str(saved.payment_frequency) } : {}),
      ...(saved.fees !== undefined && saved.fees !== null && saved.fees !== ''
        ? { fees: str(saved.fees) } : {}),
      ...(saved.funding_date ? { funding_date: str(saved.funding_date) } : {}),
      ...(saved.merchant && typeof saved.merchant === 'object' && Object.keys(saved.merchant).length > 0
        ? { merchant_prefill: saved.merchant } : {}),
    }));

    // NOTE: we deliberately do NOT mark payback/payment as "touched" here.
    // Doing so froze the wizard's live auto-calc (funding×rate → payback,
    // payback ÷ term → payment). The seeded numbers are a starting point; if the
    // operator edits the advance or rate, the blocks recompute as normal.

    if (Array.isArray(saved.refis) && saved.refis.length > 0) {
      setRefiRows(saved.refis.map((r) => {
        refiKeyRef.current += 1;
        return {
          key: refiKeyRef.current,
          old_deal_id: r.old_deal_id || '',
          deal_id_number: str(r.deal_id_number),
          label: r.label || '',
          balance_with_fees: (r.balance_with_fees ?? null),
          payoff_amount: (r.payoff_amount === null || r.payoff_amount === undefined
            ? '' : str(r.payoff_amount)),
        };
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PATCH 4 — syndication offer email recipients (Investor contacts w/ email)
  const [offerRecipients, setOfferRecipients] = useState([]);
  const [offerRecipientsLoading, setOfferRecipientsLoading] = useState(false);
  const [offerRecipientsLoaded, setOfferRecipientsLoaded] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);

  // Partner contacts = Investor contacts (Milvado, En od, …, extendable)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/contacts`, { params: { type: 'Investor' }, headers: getAuthHeaders() });
        if (!cancelled) setInvestors(Array.isArray(r.data) ? r.data : []);
      } catch (_e) {
        if (!cancelled) setInvestors([]);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  // PATCH 4 — fetch offer recipients the first time the syndication box is checked
  useEffect(() => {
    if (!form.send_for_syndication || offerRecipientsLoaded) return undefined;
    let cancelled = false;
    setOfferRecipientsLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/syndication-offer-email/recipients`, { headers: getAuthHeaders() });
        const rows = Array.isArray(r.data) ? r.data : [];
        if (!cancelled) {
          setOfferRecipients(rows);
          setSelectedRecipientIds(rows.map(x => x.id)); // all checked by default
          setOfferRecipientsLoaded(true);
        }
      } catch (_e) {
        if (!cancelled) { setOfferRecipients([]); setOfferRecipientsLoaded(true); }
      } finally {
        if (!cancelled) setOfferRecipientsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [form.send_for_syndication, offerRecipientsLoaded, getAuthHeaders]);

  const onToggleRecipient = useCallback((id) => {
    setSelectedRecipientIds(prev => (
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    ));
  }, []);

  const onToggleAllRecipients = useCallback(() => {
    setSelectedRecipientIds(prev => (
      prev.length === offerRecipients.length ? [] : offerRecipients.map(x => x.id)
    ));
  }, [offerRecipients]);

  const onChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors([]);
  }, []);

  // 09/29: picking an ISO-flagged broker switches the management-fee question ON by default
  // (Josh can still turn it off); the rate stays whatever he last typed.
  useEffect(() => {
    if (!form.broker_id) return;
    const b = (brokers || []).find(x => x.id === form.broker_id);
    if (b?.is_iso) setForm(prev => (prev.management_fee_on ? prev : { ...prev, management_fee_on: true }));
  }, [form.broker_id, brokers]);

  // Money-field change handler with live recompute (funding×rate; payback/term)
  const onMoneyChange = useCallback((field, value) => {
    setErrors([]);
    if (field === 'payback') setPaybackTouched(true);
    if (field === 'payment_amount') setPaymentTouched(true);
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      const funding = parseFloat(field === 'funding_amount' ? value : prev.funding_amount) || 0;
      const rate = parseFloat(field === 'rate' ? value : prev.rate) || 0;
      const term = parseFloat(field === 'term_days' ? value : prev.term_days) || 0;

      let payback = parseFloat(field === 'payback' ? value : prev.payback) || 0;
      if ((field === 'funding_amount' || field === 'rate') && !paybackTouched && funding > 0 && rate > 0) {
        payback = funding * rate;
        updated.payback = payback.toFixed(2);
      }
      if (field !== 'payment_amount' && !paymentTouched && payback > 0 && term > 0) {
        updated.payment_amount = (payback / term).toFixed(2);
      }
      return updated;
    });
  }, [paybackTouched, paymentTouched]);

  const onSelectMerchant = useCallback((row) => {
    setForm(prev => ({
      ...prev,
      company_id: row.id,
      deal_name: row.company_name || prev.deal_name,
      deal_shortcode: prev.deal_shortcode || row.shortcode || '',
      acknowledged_no_match: false,
    }));
    setErrors([]);
  }, []);

  const onClearMerchant = useCallback(() => {
    setForm(prev => ({ ...prev, company_id: '', deal_name: '', acknowledged_no_match: false }));
    setErrors([]);
  }, []);

  const onAcknowledgeNoMatch = useCallback((typedName) => {
    setForm(prev => ({
      ...prev,
      deal_name: typedName || prev.deal_name,
      company_id: '',
      acknowledged_no_match: true,
    }));
    setErrors([]);
  }, []);

  const onRefiAdd = useCallback(() => {
    refiKeyRef.current += 1;
    setRefiRows(prev => [...prev, {
      key: refiKeyRef.current,
      old_deal_id: '', deal_id_number: '', label: '',
      balance_with_fees: null, payoff_amount: '',
    }]);
    setErrors([]);
  }, []);

  // 08/10 (Josh): "on a refinance, funder, syndicator, commission, broker etc
  // should all port over from the refinancer" — when the FIRST source deal is
  // picked, pre-fill the Program step from it. Everything stays editable and
  // is reviewed on step 4; syndicators carry over automatically post-create
  // (patch 11 _copy_syndicators, which asks when there are 2+ sources).
  const programPrefilledRef = useRef(false);
  const prefillProgramFromDeal = useCallback(async (dealUuid) => {
    if (programPrefilledRef.current) return;
    programPrefilledRef.current = true;
    try {
      const r = await axios.get(`${API}/deals/${dealUuid}`, { headers: getAuthHeaders() });
      const src = r.data || {};
      setForm(prev => {
        const next = { ...prev };
        if (!prev.funder_id && src.funder_id) next.funder_id = src.funder_id;
        if (!prev.broker_id && src.broker_id) next.broker_id = src.broker_id;
        if (!prev.commission_points && src.commission_rate !== undefined && src.commission_rate !== null) {
          next.commission_points = String(src.commission_rate);
          next.cash_commission_paid = (parseFloat(src.commission_rate) || 0) > 0;
        }
        return next;
      });
      // Partner deal? Look up the source deal's partner ledger row (best-effort).
      try {
        const pc = await axios.get(`${API}/partner-commissions`, { headers: getAuthHeaders() });
        const entry = (pc.data?.entries || []).find(e => e.deal_id === dealUuid);
        if (entry?.partner_contact_id) {
          setForm(prev => ({
            ...prev,
            is_partner_deal: true,
            partner_contact_id: prev.partner_contact_id || entry.partner_contact_id,
            pfs_participation_pct:
              prev.pfs_participation_pct !== '' && prev.pfs_participation_pct !== null && prev.pfs_participation_pct !== undefined
                ? prev.pfs_participation_pct
                : String(entry.pfs_participation_pct ?? ''),
          }));
        }
      } catch (_e) { /* partner lookup is best-effort */ }
      toast.success('Funder, broker & commission carried over from the refinanced deal — review on the Program step');
    } catch (_e) {
      programPrefilledRef.current = false; // allow retry on the next pick
    }
  }, [getAuthHeaders]);

  const onRefiUpdate = useCallback((index, row) => {
    setRefiRows(prev => prev.map((r, i) => (i === index ? row : r)));
    if (row.old_deal_id) prefillProgramFromDeal(row.old_deal_id);
    setErrors([]);
  }, [prefillProgramFromDeal]);

  const onRefiRemove = useCallback((index) => {
    setRefiRows(prev => prev.filter((_, i) => i !== index));
    setErrors([]);
  }, []);

  // Derived totals + partner math (live everywhere)
  const totals = useMemo(() => {
    const funding = parseFloat(form.funding_amount) || 0;
    const fees = parseFloat(form.fees) || 0;
    const totalPayoff = refiRows.reduce((s, r) => s + (parseFloat(r.payoff_amount) || 0), 0);
    return {
      funding,
      fees,
      totalPayoff: Math.round(totalPayoff * 100) / 100,
      netAdvance: funding - fees - totalPayoff,
      payback: parseFloat(form.payback) || 0,
      payment: parseFloat(form.payment_amount) || 0,
      refiCount: refiRows.length,
      estimatedEndDate: computeEstimatedEndDate(form.funding_date, form.term_days, form.payment_frequency),
    };
  }, [form.funding_amount, form.fees, form.payback, form.payment_amount, form.funding_date, form.term_days, form.payment_frequency, refiRows]);

  const partnerCalc = useMemo(() => {
    const funding = totals.funding;
    const fees = totals.fees;
    const points = parseFloat(form.commission_points) || 0;
    const effectivePoints = form.cash_commission_paid ? points : 0;
    if (!form.is_partner_deal) {
      return { dealClass: 'standard', pfsPct: 100, partnerPct: 0, wireDue: 0, cmOwed: 0, effectivePoints };
    }
    const pfsPct = Math.min(100, Math.max(0, parseFloat(form.pfs_participation_pct) || 0));
    const partnerPct = Math.round((100 - pfsPct) * 100) / 100;
    const dealClass = pfsPct === 0 ? 'management' : 'standard';
    const wireDue = Math.round((funding * partnerPct / 100 - fees * partnerPct / 100) * 100) / 100;
    const cmOwed = pfsPct === 0 ? 0 : Math.round(((effectivePoints / 100) * (funding * pfsPct / 100)) * 100) / 100;
    return { dealClass, pfsPct, partnerPct, wireDue, cmOwed, effectivePoints };
  }, [form.is_partner_deal, form.pfs_participation_pct, form.commission_points, form.cash_commission_paid, totals.funding, totals.fees]);

  // originator_label auto-derivation (spec: "originator_label auto")
  const deriveOriginatorLabel = () => {
    if (form.is_partner_deal && form.partner_contact_id) {
      const p = investors.find(i => i.id === form.partner_contact_id);
      const n = (p?.company_name || '').toLowerCase().replace(/\s+/g, '');
      if (n.includes('milvado')) return 'milvado';
      if (n.includes('enod')) return 'enod';
      if (n.includes('dib')) return 'dib';
      return 'other';
    }
    return 'pfs';
  };

  const next = () => {
    const errs = validateStep(step, form, refiRows);
    if (errs.length > 0) { setErrors(errs); return; }
    setErrors([]);
    setStep(s => Math.min(s + 1, 4));
  };

  const prev = () => { setErrors([]); setStep(s => Math.max(s - 1, 1)); };

  const handleSubmit = async () => {
    // Re-validate everything before the atomic write
    const allErrs = [1, 2, 3, 4].flatMap(s => validateStep(s, form, refiRows));
    if (allErrs.length > 0) { setErrors(allErrs); return; }
    setSubmitting(true);
    try {
      const fundingAmount = totals.funding;
      const payback = totals.payback;
      const effectiveRate = partnerCalc.effectivePoints; // 0 when favor-funded
      const estEnd = computeEstimatedEndDate(form.funding_date, form.term_days, form.payment_frequency);

      // Matches routes/deal_intake.py::DealIntakeRequest exactly.
      const payload = {
        deal: {
          deal_id: form.deal_id.trim(),
          deal_shortcode: form.deal_shortcode.trim(),
          deal_name: form.deal_name.trim(),
          funding_date: `${form.funding_date}T00:00:00Z`,
          funding_amount: fundingAmount,
          rate: parseFloat(form.rate) || 0,
          payback,
          balance: payback,
          with_fees: payback,
          payment_amount: totals.payment,
          term_days: parseFloat(form.term_days) || 0,
          payment_frequency: form.payment_frequency,
          fees: totals.fees,
          refi_balance_payoff: totals.totalPayoff,
          net_funding: totals.netAdvance,
          broker_id: form.broker_id,
          funder_id: form.funder_id,
          company_id: form.company_id || null,
          originator_label: deriveOriginatorLabel(),
          originator_id: null,
          commission_rate: effectiveRate,
          commission_type: 'iso',
          is_iso_originated: !!(brokers || []).find(b => b.id === form.broker_id)?.is_iso,
          management_fee_rate: form.management_fee_on ? (parseFloat(form.management_fee_rate) || 0) : null,
          deal_class: partnerCalc.dealClass,
          // ALWAYS computed and sent: funding_date + ceil(term_days × 1.4) days
          estimated_end_date: estEnd ? `${estEnd}T00:00:00Z` : null,
          funder_percentage: 100,
          status: 'Funded',
          payments_made: 0,
          percent_paid_thru: 0,
          payments_til_pif: 0,
        },
        refis: refiRows.map(r => ({
          old_deal_id: r.old_deal_id,
          payoff_amount: parseFloat(r.payoff_amount) || 0,
        })),
        partner: form.is_partner_deal && form.partner_contact_id ? {
          partner_contact_id: form.partner_contact_id,
          pfs_participation_pct: partnerCalc.pfsPct,
          wire_due: partnerCalc.wireDue,
          cm_owed: partnerCalc.cmOwed,
        } : null,
        send_for_syndication: !!form.send_for_syndication,
        reuse_syndicators: !!form.reuse_syndicators,
        // Contract identity → linked merchant, missing fields only (backend rule).
        merchant_prefill: form.merchant_prefill || null,
      };

      const res = await axios.post(`${API}/deals/intake`, payload, { headers: getAuthHeaders() });
      toast.success(res.data?.message || 'Deal created');
      if (Array.isArray(res.data?.merchant_fields_filled) && res.data.merchant_fields_filled.length > 0) {
        toast.success(`Merchant record filled from the contract: ${res.data.merchant_fields_filled.map(k => k.replace(/_/g, ' ')).join(', ')}`);
      }
      if (Array.isArray(res.data?.merchant_field_conflicts) && res.data.merchant_field_conflicts.length > 0) {
        toast(`Contract differs from the merchant record on: ${res.data.merchant_field_conflicts.map(k => k.replace(/_/g, ' ')).join(', ')} — CRM values kept`, { duration: 8000 });
      }
      if (res.data?.syndicators_copied > 0) {
        toast.success(`${res.data.syndicators_copied} syndicator(s) carried over from the refinanced deal`);
      } else if (res.data?.needs_syndicator_choice) {
        toast.info('Multiple source deals — open the new deal\'s Syndication tab to choose which book to copy.');
      }

      const newDealUuid = res.data?.deal?.id;
      if (form.send_for_syndication && newDealUuid) {
        // PATCH 4 — email the offer to the chosen syndicators. Non-blocking:
        // a send failure toasts but never blocks the redirect below.
        if (selectedRecipientIds.length > 0) {
          try {
            const sendRes = await axios.post(`${API}/syndication-offer-email/send`, {
              deal_id: newDealUuid,
              recipient_contact_ids: selectedRecipientIds,
            }, { headers: getAuthHeaders() });
            const sent = sendRes.data?.sent || 0;
            const results = Array.isArray(sendRes.data?.results) ? sendRes.data.results : [];
            if (sent > 0) toast.success(`Syndication offer emailed to ${sent} syndicator(s)`);
            results.filter(r => !r.success).forEach(r => {
              toast.error(`Offer email failed for ${r.name || r.contact_id}: ${r.error || 'unknown error'}`);
            });
          } catch (sendErr) {
            const d = sendErr.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'Failed to send the syndication offer email');
          }
          // brief pause so the operator sees the send toast before navigation
          await new Promise(resolve => setTimeout(resolve, 900));
        } else {
          toast('No syndicators selected — offer email skipped');
        }
        // Hooks the existing syndication_offers flow (allocate→request→confirm)
        window.location.href = `/deals/${newDealUuid}/offering`;
        return;
      }
      if (typeof onCancel === 'function') onCancel();
      // Parent's fetchDeals lives inside its onSubmit path we no longer use;
      // hard refresh guarantees the table shows the new deal.
      window.location.reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? (detail[0]?.msg || JSON.stringify(detail))
        : (err.message || 'Failed to create deal');
      setErrors([msg]);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="deal-wizard">
      <StepIndicator current={step} />

      {(errors.length > 0 || externalError) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {externalError || errors.join('. ')}
          </AlertDescription>
        </Alert>
      )}

      {step === 1 && (
        <Step1
          form={form}
          onChange={onChange}
          onSelectMerchant={onSelectMerchant}
          onClearMerchant={onClearMerchant}
          onAcknowledgeNoMatch={onAcknowledgeNoMatch}
        />
      )}
      {step === 2 && (
        <Step2
          form={form}
          onChange={onChange}
          onMoneyChange={onMoneyChange}
          refiRows={refiRows}
          onRefiUpdate={onRefiUpdate}
          onRefiAdd={onRefiAdd}
          onRefiRemove={onRefiRemove}
          totals={totals}
        />
      )}
      {step === 3 && (
        <Step3
          form={form}
          onChange={onChange}
          brokers={brokers}
          funders={funders}
          investors={investors}
          partnerCalc={partnerCalc}
        />
      )}
      {step === 4 && (
        <Step4
          form={form}
          onChange={onChange}
          totals={totals}
          partnerCalc={partnerCalc}
          brokers={brokers}
          funders={funders}
          investors={investors}
          recipients={offerRecipients}
          recipientsLoading={offerRecipientsLoading}
          selectedRecipientIds={selectedRecipientIds}
          onToggleRecipient={onToggleRecipient}
          onToggleAllRecipients={onToggleAllRecipients}
        />
      )}

      <div className="flex justify-between pt-4 border-t">
        <div>
          {step > 1 && (
            <Button variant="outline" onClick={prev} data-testid="wizard-prev-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          {step < 4 ? (
            <Button onClick={next} data-testid="wizard-next-btn">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} data-testid="wizard-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Create Deal
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealWizard;
